import { Injectable } from '@angular/core';
import * as models from '@daas-consulting/production/remitos/frontend/models';
import * as store from '@daas-consulting/production/remitos/frontend/store';
import { AdditionalArticleModel } from '@daas-consulting/production/remitos/frontend/store';
import {
  RemitosFormPageViewContract,
  RemitosFormPageViewContractId,
} from '@daas-consulting/production/remitos/frontend/view_contracts';
import { Navigate } from '@ngxs/router-plugin';
import { Select, Store } from '@ngxs/store';
import { StateReset } from 'ngxs-reset-plugin';
import { Observable } from 'rxjs';

export { RemitosFormPageViewContractId };

@Injectable()
export class RemitosFormPageViewModel implements RemitosFormPageViewContract {
  @Select(store.form.selectors.RemitosFormSelectors.isSubmitDisabled)
  isSubmitDisabled$!: Observable<boolean>;

  @Select(store.form.selectors.RemitosFormSelectors.availablePrescriptions)
  availablePrescriptions$!: Observable<models.AvailablePrescriptionModel[]>;

  @Select(store.form.selectors.RemitosFormSelectors.institutions)
  institutions$!: Observable<models.InstitutionFormData[]>;

  @Select(store.form.selectors.RemitosFormSelectors.lines)
  lines$!: Observable<models.LineFormData[]>;

  @Select(store.form.selectors.RemitosFormSelectors.articles)
  articles$!: Observable<models.ArticleFormData[]>;

  @Select(store.form.selectors.RemitosFormSelectors.activePurchaseOrders)
  activePurchaseOrders$!: Observable<models.PurchaseOrderFormData[]>;

  @Select(store.form.selectors.RemitosFormSelectors.selectedInstitution)
  selectedInstitution$!: Observable<models.InstitutionFormData | null>;

  @Select(store.form.selectors.RemitosFormSelectors.selectedPurchaseOrder)
  selectedPurchaseOrder$!: Observable<models.PurchaseOrderFormData | null>;

  @Select(store.form.selectors.RemitosFormSelectors.additionalArticles)
  additionalArticles$!: Observable<AdditionalArticleModel[]>;

  @Select(
    store.form.selectors.RemitosFormSelectors.filteredAvailablePrescriptions
  )
  filteredAvailablePrescriptions$!: Observable<
    models.AvailablePrescriptionModel[]
  >;

  @Select(store.form.selectors.RemitosFormSelectors.lineSummaries)
  lineSummaries$!: Observable<models.LineSummaryModel[]>;

  @Select(store.form.selectors.RemitosFormSelectors.selectedLineId)
  selectedLineId$!: Observable<string | null>;

  @Select(store.form.selectors.RemitosFormSelectors.selectedLine)
  selectedLine$!: Observable<models.LineFormData | null>;

  @Select(store.form.selectors.RemitosFormSelectors.selectedPatientName)
  selectedPatientName$!: Observable<string | null>;

  @Select(store.form.selectors.RemitosFormSelectors.patientSummaries)
  patientSummaries$!: Observable<models.PatientSummaryModel[]>;

  readonly #store: Store;

  constructor(store: Store) {
    this.#store = store;
  }

  load(): void {
    this.#store.dispatch(new store.form.actions.GetData());
  }

  loadPrescriptions(institutionId: string): void {
    this.#store.dispatch(new store.form.actions.SetInstitution(institutionId));
  }

  setPurchaseOrder(purchaseOrderId: string): void {
    this.#store.dispatch(
      new store.form.actions.SetPurchaseOrder(purchaseOrderId)
    );
  }

  setLine(lineId: string): void {
    this.#store.dispatch(new store.form.actions.SetLine(lineId));
  }

  setPatient(patientName: string): void {
    this.#store.dispatch(new store.form.actions.SetPatient(patientName));
  }

  togglePrescription(prescriptionId: string, selected: boolean): void {
    this.#store.dispatch(
      new store.form.actions.TogglePrescriptionSelection({
        prescriptionId,
        selected,
      })
    );
  }

  toggleAllPrescriptions(selected: boolean): void {
    this.#store.dispatch(
      new store.form.actions.ToggleAllPrescriptionsSelection({ selected })
    );
  }

  addAdditionalArticle(
    articleId: string,
    quantity: number,
    lot: string | null,
    expirationDate: string | null
  ): void {
    this.#store.dispatch(
      new store.form.actions.AddAdditionalArticle({
        articleId,
        quantity,
        lot,
        expirationDate,
      })
    );
  }

  removeAdditionalArticle(
    articleId: string,
    lot: string | null,
    expirationDate: string | null
  ): void {
    this.#store.dispatch(
      new store.form.actions.RemoveAdditionalArticle({
        articleId,
        lot,
        expirationDate,
      })
    );
  }

  submit(): void {
    this.#store.dispatch(new store.form.actions.Save());
  }

  cancel(): void {
    this.#store.dispatch(new Navigate(['/remitos/list']));
  }

  destroy(): void {
    this.#store.dispatch(new StateReset(store.form.state.RemitosFormState));
  }
}
